/**
 * Drums: what a note head means when the staff has no pitch on it.
 *
 * A percussion staff is written the same way as any other -- heads on lines and spaces,
 * stems, beams, bars of the right length -- but a line is an instrument rather than a
 * pitch. The snare is written in the third space not because it sounds C5 but because
 * that is where drummers have agreed to put it, and a kit says which line is which drum.
 *
 * So a drum note keeps an ordinary position in {@link NotePitch} -- `step` and `octave`
 * are read as staff places, exactly as a pitched note's are -- and names the drum it is
 * in {@link NotePitch.drum}. Everything that only cares where a head sits (spacing,
 * beaming, selection, the clipboard) therefore needs to know nothing about percussion,
 * and everything that cares what it sounds looks the name up in the part's kit.
 *
 * Two drums may share a line: the closed and the open hi-hat are both written above the
 * staff and told apart by their heads. That is why a note names its drum rather than
 * being read off its position, and why the lookup by position is only what note *entry*
 * reaches for when a letter is typed.
 */

import { STEPS } from "./pitch";
import type { Step } from "./pitch";

/**
 * The shape a head is drawn with.
 *
 * Cymbals and hi-hats take a cross, a rim click a diamond or a cross depending on the
 * house style, and the drums themselves an ordinary head. These are MusicXML's names.
 */
export type NoteHead = "normal" | "x" | "circle-x" | "diamond" | "triangle" | "slash";

/** One drum of a kit: where it is written, what it is called and what it sounds. */
export interface DrumNote {
  /** What the drum is called. Unique within a kit; this is what a note names. */
  readonly name: string;
  /** General MIDI percussion key, 35-81. Not a pitch: it chooses the instrument. */
  readonly midi: number;
  /** The staff place the head is written on, read as a pitched note's would be. */
  readonly step: Step;
  readonly octave: number;
  readonly notehead: NoteHead;
  /**
   * Drawn with the small circle that means the instrument is left to ring.
   *
   * The open hi-hat is the reason it exists: it is struck with the pedal up, is written on
   * the same line as the closed one, and the two are told apart by that circle over the
   * head. A head shape cannot carry it, because the head is already saying which cymbal it
   * is.
   */
  readonly open: boolean;
  /**
   * Which way the stem goes.
   *
   * A kit is written in two voices by convention -- hands up, feet down -- so the stem of
   * a drum is a property of the kit rather than of the bar it lands in. `"auto"` leaves it
   * to the engraver, which is what a one-line instrument wants.
   */
  readonly stem: "auto" | "up" | "down";
}

/** The drums one part is written with. */
export interface PercussionKit {
  readonly name: string;
  readonly notes: readonly DrumNote[];
}

/** Where a drum is written, on the same ladder {@link diatonicOf} counts pitches by. */
export function drumPlace(drum: DrumNote): number {
  return drum.octave * 7 + STEPS.indexOf(drum.step);
}

/** The drum of that name, or `null` when the kit has none. */
export function drumNamed(kit: PercussionKit, name: string): DrumNote | null {
  return kit.notes.find((drum) => drum.name === name) ?? null;
}

/**
 * The drum a staff place means, or `null` for an empty kit.
 *
 * Exactly on the line if the kit has one there, and otherwise the nearest drum to it, so
 * that typing a letter on a drum staff always writes something rather than refusing. Ties
 * go to the lower drum, which keeps the answer stable as a caller walks up the staff.
 */
export function drumAt(kit: PercussionKit, step: Step, octave: number): DrumNote | null {
  const place = octave * 7 + STEPS.indexOf(step);
  let best: DrumNote | null = null;
  let distance = Number.POSITIVE_INFINITY;
  for (const drum of kit.notes) {
    const away = Math.abs(drumPlace(drum) - place);
    if (away < distance) {
      best = drum;
      distance = away;
    }
  }
  return best;
}

/**
 * The drum a General MIDI percussion key plays, or `null` when the kit has none there.
 *
 * This is the lookup a pad reaches for. A drum pad sends the number of the instrument it
 * is rather than a pitch, so the number names the drum outright and there is nothing to
 * spell and nothing to be near: an exact match or nothing. Two drums of one kit never
 * share a key, so the first match is the only one.
 */
export function drumByMidi(kit: PercussionKit, midi: number): DrumNote | null {
  return kit.notes.find((drum) => drum.midi === midi) ?? null;
}

/**
 * The kit from the bottom of the staff up, which is the order an arrow key walks it in.
 *
 * Two drums on one line keep the order the kit lists them in, so that walking up and then
 * down again arrives back where it started.
 */
export function drumOrder(kit: PercussionKit): readonly DrumNote[] {
  return kit.notes
    .map((drum, index) => ({ drum, index }))
    .sort((a, b) => drumPlace(a.drum) - drumPlace(b.drum) || a.index - b.index)
    .map((entry) => entry.drum);
}

/**
 * The drum `count` places along the staff from this one, stopping at the ends of the kit.
 *
 * An arrow on a drum staff means "the next drum", not "a semitone": there is no pitch to
 * raise. Stopping rather than wrapping keeps a held key from walking a crash back round
 * to the kick.
 */
export function drumStepped(kit: PercussionKit, from: DrumNote, count: number): DrumNote {
  const order = drumOrder(kit);
  const at = order.indexOf(from);
  if (at < 0) return from;
  const to = Math.max(0, Math.min(order.length - 1, at + count));
  return order[to] ?? from;
}

/** The drum a note names, or `null` when it is an ordinary pitch or the kit has lost it. */
export function drumOf(kit: PercussionKit | null, drum: string | null): DrumNote | null {
  if (kit === null || drum === null) return null;
  return drumNamed(kit, drum);
}
